import { useEffect, useRef, useState } from "react";
import { useController, FieldValues } from "react-hook-form";

import { AddressAutocompleteProps, Places } from "./types";
import Autocomplete from "../Autocomplete";
import { applyAddress1SuggestionFallback, applyPostalCodeSuggestionFallback, placeToFormData } from "./utils";
import getPlaces from "./loadGooglePlaces";

type Item = { value: string; label: string };

function renderItem({ label }: Item) {
  return <span>{label}</span>;
}

function AddressAutocompleteInput<T extends FieldValues>({
  name,
  label,
  control,
  rules,
  country,
  onSuggest,
  onReset,
  required,
  ...inputProps
}: AddressAutocompleteProps<T>) {
  const [data, setData] = useState<Item[]>([]);

  const attributionRef = useRef<HTMLDivElement>(null);

  const placesLibRef = useRef<Places>();

  const {
    field: { ref: formRef, ...field },
    fieldState: { error },
    formState: { isSubmitting }
  } = useController({ control, name, rules: { required: !!required, ...rules } });

  useEffect(() => {
    getPlaces(attributionRef.current!).then((places) => {
      placesLibRef.current = places;
    });
  }, []);

  // FIXME: debounce?
  const getPlacePreditions = (input: string) => {
    const places = placesLibRef.current;
    if (places) return places.getPlacePredictions({ input, types: ["address"], componentRestrictions: { country } });
    else throw "cannot load places lib";
  };

  async function onChangeText(input: string) {
    field.onChange(input);

    if (input) {
      const response = await getPlacePreditions(input);
      if (response?.predictions.length)
        setData(response.predictions.map(({ description, place_id }) => ({ value: place_id, label: description })));
    }
  }

  function onPlaceDetails(place: google.maps.places.PlaceResult, suggestionLabel: string) {
    const parsedAddress = placeToFormData(place);
    const withAddressLineFallback = applyAddress1SuggestionFallback(parsedAddress, suggestionLabel);
    onSuggest(applyPostalCodeSuggestionFallback(withAddressLineFallback, suggestionLabel));
  }

  function onSelect(value: Item) {
    field.onChange(value.label);
    placesLibRef.current?.getDetails({ placeId: value.value, fields: ["address_components"] }, (place) => {
      if (place) onPlaceDetails(place, value.label);
    });
  }

  return (
    <>
      <div ref={attributionRef} />
      <Autocomplete
        data={data}
        error={!!error}
        errorText={error?.message}
        label={label}
        onChangeText={onChangeText}
        onSelect={onSelect}
        renderItem={renderItem}
        reset={onReset}
        leadingIcon={null}
        query={field.value}
        required={required}
        disabled={isSubmitting}
        {...inputProps}
      />
    </>
  );
}

export default AddressAutocompleteInput;
